import { Route } from 'react-router-dom';
import { useNavigationConfig } from '../services/navigationConfig';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { QueryBoundary } from '../components/QueryBoundary';

/**
 * Hook that generates routes for all navigation menu items dynamically from app registry.
 *
 * All local apps (those without moduleUrl) must have a component defined in the app registry.
 * Remote apps (those with moduleUrl) are handled by RemoteAppShell routes.
 *
 * To replace a placeholder page with a real component:
 * 1. Create your new page component (e.g., WorkflowsPage.tsx)
 * 2. Import it in appRegistry.ts
 * 3. Update the app entry to use your component instead of PlaceholderPage
 *
 * Example:
 * ```typescript
 * // In appRegistry.ts
 * import { WorkflowsPage } from '../pages/WorkflowsPage';
 *
 * {
 *   id: 'workflows',
 *   component: WorkflowsPage, // Replace PlaceholderPage with your component
 *   // ... other fields
 * }
 * ```
 */
export function useNavigationRoutes() {
  const navigationConfig = useNavigationConfig();

  return navigationConfig.sections.flatMap((section) =>
    section.items
      // Skip remote apps - they're handled by RemoteAppShell routes
      .filter((item) => 'component' in item && item.component)
      .map((item) => {
        // All local apps must have a component defined in the registry
        const PageComponent = item.component;
        if (!PageComponent) {
          console.warn(
            `[NavigationRoutes] App "${item.id}" is missing a component. This should not happen for local apps.`
          );
          return null;
        }

        // Root-path local apps use exact match — the splat is for remote
        // apps' sub-paths, and using it here shadows the App-level catch-all
        // <Navigate to="/"> for every stray URL (e.g. /auth0/callback).
        const routePath = item.path === '/' ? '/' : `${item.path}/*`;

        return (
          <Route
            key={item.path}
            path={routePath}
            element={
              <QueryBoundary
                loadingFallback={<LoadingSpinner />}
                errorMessage="Failed to load page"
              >
                <PageComponent />
              </QueryBoundary>
            }
          />
        );
      })
      .filter((route): route is JSX.Element => route !== null)
  );
}
